"use client";

import { Factory, Truck, Package, Globe, Warehouse, ShieldCheck, CircleCheck as CheckCircle2 } from 'lucide-react';
import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SupplyChainMilestones() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const timelineRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  const steps = [{
    number: "01",
    title: "MANUFACTURING",
    subtitle: "GLOBAL PARTNERSHIPS",
    description: "We maintain direct, long-term manufacturing partnerships in China, ensuring every aluminum profile meets rigorous international quality standards before it ever leaves the factory floor.",
    fact: "DIRECT FACTORY-TO-WAREHOUSE SUPPLY CHAIN",
    icon: Factory,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/steel_texture_1.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/steel_texture_1.webp-wvc-srcset",
    sizes: "(max-width: 768px) 200px, 240px",
    rotation: "0deg"
  }, {
    number: "02",
    title: "LOGISTICS",
    subtitle: "MARITIME FREIGHT",
    description: "Our dedicated logistics team manages bulk sea freight shipments, optimizing transit times and ensuring the structural integrity of our architectural hardware during international transit.",
    fact: "OPTIMIZED BULK SHIPPING ROUTES",
    icon: Globe,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/steel_texture_2.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/steel_texture_2.png-wvc-srcset",
    sizes: "(max-width: 768px) 200px, 240px",
    rotation: "0deg"
  }, {
    number: "03",
    title: "WAREHOUSING",
    subtitle: "UAE HUB",
    description: "Strategically located in Sharjah and Ajman, our high-capacity warehouses serve as the central nervous system for our UAE-wide distribution network, keeping stock ready for immediate dispatch.",
    fact: "EXPANSIVE STORAGE IN SHARJAH & AJMAN",
    icon: Warehouse,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/steel_texture_3.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/steel_texture_3.webp-wvc-srcset",
    sizes: "(max-width: 768px) 200px, 240px",
    rotation: "0deg"
  }, {
    number: "04",
    title: "QUALITY",
    subtitle: "PRECISION CHECK",
    description: "Upon arrival, every batch undergoes a secondary inspection process to verify alloy composition, surface finish, and dimensional accuracy, guaranteeing engineered precision for your projects.",
    fact: "100% BATCH INSPECTION PROTOCOL",
    icon: ShieldCheck,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/steel_texture_4.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/steel_texture_4.webp-wvc-srcset",
    sizes: "(max-width: 768px) 200px, 240px",
    rotation: "0deg"
  }, {
    number: "05",
    title: "DISTRIBUTION",
    subtitle: "LOCAL FLEET",
    description: "Our local fleet ensures rapid, reliable delivery across the UAE, including our expanding operations in Al Ain, providing contractors with the materials they need, exactly when they need them.",
    fact: "FAST-TRACK DELIVERY ACROSS UAE",
    icon: Truck,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/steel_texture_5.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/steel_texture_5.webp-wvc-srcset",
    sizes: "(max-width: 768px) 200px, 240px",
    rotation: "0deg"
  }, {
    number: "06",
    title: "FULFILLMENT",
    subtitle: "PROJECT READY",
    description: "From bulk aluminum profiles to specialized architectural hardware, we provide the essential components that form the backbone of modern commercial construction across the region.",
    fact: "SUPPORTING MAJOR UAE INFRASTRUCTURE",
    icon: Package,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/steel_texture_6.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/steel_texture_6.png-wvc-srcset",
    sizes: "(max-width: 768px) 200px, 240px",
    rotation: "0deg"
  }];
  useEffect(() => {
    const handleScroll = () => {
      const el = timelineRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const windowH = window.innerHeight;
      const total = rect.height + windowH;
      const scrolled = windowH - rect.top;
      const pct = Math.max(0, Math.min(100, scrolled / total * 100));
      setProgress(pct);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="378" id="supply-chain-milestones" className="bg-white text-foreground relative overflow-hidden py-20 md:py-32">
      <div className="relative mx-auto max-w-7xl px-4 md:px-8">
        <div className="bg-card text-card-foreground shadow-lg px-6 py-16 md:px-16 md:py-24">
          <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <h1 className="font-default font-bold text-4xl md:text-6xl lg:text-7xl leading-[1.1] tracking-tighter mb-8">
              Industrial Fit-Out Supply Expertise Since 2023
            </h1>
            <div className="mt-6 flex justify-center">
              <svg width="180" height="14" viewBox="0 0 180 14" fill="none" className="text-ring" aria-hidden="true">
                <path d="M2 8 Q 45 1, 90 7 T 178 6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" />
              </svg>
            </div>
            <p className="mt-8 font-default text-base md:text-lg leading-relaxed text-muted-foreground max-w-xl mx-auto">
              World Sky Building Materials Trading LLC bridges the gap between global manufacturing precision and local logistical mastery, ensuring your projects are built on a foundation of reliability.
            </p>
            <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 text-left max-w-2xl mx-auto">
              {["Strategic Global Sourcing Capabilities", "Rigorous Quality Assurance Process", "High-Volume Inventory Depth", "Unmatched Vendor Reliability"].map((item, idx) => <li key={idx} className="flex items-center gap-3 text-sm font-mono uppercase tracking-wider text-muted-foreground">
                  <CheckCircle2 className="size-5 text-ring shrink-0" />
                  {item}
                </li>)}
            </ul>
          </div>

          <div ref={timelineRef} className="relative mt-20 md:mt-28">
            <div className="pointer-events-none absolute left-5 md:left-1/2 top-0 bottom-0 -translate-x-1/2 hidden md:block" aria-hidden="true">
              <div className="relative h-full w-[2px]">
                <div className="absolute inset-0 bg-border" />
                <div className="absolute left-0 top-0 w-full transition-[height] duration-300 ease-out bg-ring" style={{
                height: `${progress}%`
              }} />
              </div>
            </div>

            <ol className="space-y-20 md:space-y-32">
              {steps.map((step, i) => {
              const isLeft = i % 2 === 0;
              const Icon = step.icon;
              return <TimelineStep key={step.number} data-index={i} isLeft={isLeft} step={step} Icon={Icon} />;
            })}
            </ol>

            <div className="relative mt-20 md:mt-28 flex flex-col items-center text-center">
              <div className="hidden md:block absolute left-1/2 -top-10 -translate-x-1/2">
                <svg width="2" height="40" aria-hidden="true">
                  <line x1="1" y1="0" x2="1" y2="40" stroke="hsl(199 89% 48%)" strokeWidth="2" strokeDasharray="6 8" />
                </svg>
              </div>
              <div className="flex h-20 w-20 items-center justify-center rounded-lg border border-ring bg-secondary shadow-md">
                <Package className="h-10 w-10 text-ring" strokeWidth={1.5} aria-hidden="true" />
              </div>
              <p className="mt-8 font-default font-semibold text-3xl md:text-4xl tracking-tighter">
                READY FOR YOUR NEXT PROJECT.
              </p>
              <p className="mt-4 font-mono text-xs tracking-[0.25em] text-muted-foreground uppercase">
                World Sky Building Materials Trading LLC
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
type StepData = {
  number: string;
  title: string;
  subtitle: string;
  description: string;
  fact: string;
  image: string;
  srcset: string;
  sizes: string;
  rotation: string;
};
function TimelineStep({
  isLeft,
  step,
  Icon,
  ...rest
}: {
  isLeft: boolean;
  step: StepData;
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
    "aria-hidden"?: boolean;
  }>;
} & React.HTMLAttributes<HTMLLIElement>) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLLIElement>({
    threshold: 0.25
  });
  return <li ref={ref} {...rest} className={`relative grid md:grid-cols-2 md:gap-12 items-center transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="absolute left-5 md:left-1/2 top-0 md:top-1/2 -translate-x-1/2 md:-translate-y-1/2 z-10" aria-hidden="true">
        <div className={`relative flex h-6 w-6 items-center justify-center rounded-lg bg-ring shadow-md ring-4 ring-card ${isVisible ? "animate-pulse" : ""}`}>
          <span className="font-mono text-[10px] font-bold text-background">
            {step.number}
          </span>
        </div>
      </div>

      <div className={`pl-14 md:pl-0 ${isLeft ? "md:col-start-1 md:pr-12 md:text-right" : "md:col-start-2 md:pl-12 md:text-left"}`}>
        <div className={`flex items-baseline gap-4 mb-4 ${isLeft ? "md:justify-end" : "md:justify-start"}`}>
          <span className="font-default font-bold text-6xl md:text-[96px] leading-none tracking-tighter text-transparent" style={{
          WebkitTextStroke: "1.5px hsl(199 89% 48%)"
        }} aria-hidden="true">
            {step.number}
          </span>
          <Icon className="h-8 w-8 md:h-10 md:w-10 text-ring" strokeWidth={1.5} aria-hidden={true} />
        </div>

        <h3 className="font-default font-semibold text-3xl md:text-[40px] leading-[1.05] tracking-tighter">
          {step.title}
          <br />
          <span className="text-muted-foreground">{step.subtitle}</span>
        </h3>

        <p className={`mt-5 font-default text-base leading-relaxed text-muted-foreground max-w-[380px] ${isLeft ? "md:ml-auto" : ""}`}>
          {step.description}
        </p>

        <p className={`mt-6 font-mono text-[11px] md:text-xs tracking-[0.25em] text-ring uppercase border-t border-border pt-4 max-w-[380px] ${isLeft ? "md:ml-auto" : ""}`}>
          {step.fact}
        </p>
      </div>

      <div className={`mt-8 md:mt-0 pl-14 md:pl-0 ${isLeft ? "md:col-start-2 md:pl-12" : "md:col-start-1 md:row-start-1 md:pr-12 md:flex md:justify-end"}`}>
        <div className="inline-block bg-secondary p-3 shadow-lg border border-border">
          <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={`${step.title} ${step.subtitle}`} loading="lazy" decoding="async" className="block h-[200px] w-[200px] md:h-[240px] md:w-[240px] object-cover" />
          <p className="mt-3 font-mono text-[10px] tracking-[0.2em] text-muted-foreground uppercase text-center">
            Phase {step.number} — {step.title}
          </p>
        </div>
      </div>
    </li>;
}